import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  DialogContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
} from "@mui/material";
import { User, UserResponse } from "../../types/users";
import { userService } from "../../apis/users";
import NotificationPopup from "./NotificationPopup";
import { EditorProvider } from "react-simple-wysiwyg";
import { jwtDecode } from "jwt-decode";
import { formatTime } from "../../utils/util";
import { getReferredUsersById } from "../../apis/referralPlans";

const RiderTable: React.FC<{ onSelectionChange?: (users: User[]) => void }> = ({ onSelectionChange }) => {
  const [riders, setRiders] = useState<User[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);
  const [selectedRiders, setSelectedRiders] = useState<string[]>([]);
  const [role, setRole] = useState("all");
  const [minRides, setMinRides] = useState<number | "">("");
  const [maxRides, setMaxRides] = useState<number | "">("");
  const [country, setCountry] = useState("all");
  const [filtersVisible, setFiltersVisible] = useState(false); // State to toggle filters visibility
  const countries: Record<string, string> = {
    uk: "UK",
    france: "FRANCE",
    "ivory-coast": "Ivory Coast",
  };
  const [adminCountry, setAdminCountry] = useState<string>("all");
  // State for popup
  const [popupOpen, setPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referredUsers, setReferredUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const loadRiders = async (adminCountryLocal: string) => {
    try {
      let tempCountry = country;
      if (adminCountryLocal != "all") {
        tempCountry = adminCountryLocal;
      }
      // Construct the params object conditionally
      const params: any = {
        search,
        page: page + 1, // API uses 1-based page index
        limit: rowsPerPage,
        minRides: minRides,
        maxRides: maxRides,
        countryCode: tempCountry === "all" ? "" : tempCountry,
      };

      params.userType = "rider";
      params.role = role === "all" ? "" : role;

      const data: UserResponse = await userService.getUsers(params);
      setTotal(data.total);
      setRiders(data.users);
    } catch (error) {
      console.error("Error fetching riders:", error);
    }
  };

  const fetchReferredUsers = async (userId: string) => {
    setLoading(true);
    try {
      const response: any = await getReferredUsersById(userId);
      if (!response) {
        setLoading(false);
        return;
      }
      // Check if the response is valid
      
      if (response.status) {
        setReferredUsers(response.data.referred_users);
      } else {
        setReferredUsers([]);
      }
    } catch (error) {
      console.error("Error fetching referred users:", error);
      setReferredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReferralClick = (userId: string) => {
    setSelectedUserId(userId);
    setPopupOpen(true);
    fetchReferredUsers(userId);
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
    setReferredUsers([]);
    setSelectedUserId(null);
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded: any = jwtDecode(token);
      setAdminCountry(decoded.countryCode);
      loadRiders(decoded.countryCode);
    }
  }, [page, rowsPerPage, search, role, minRides, maxRides, country]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0); // Reset to first page on search
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page on rows per page change
  };
  const handleFilterChange =
    (setter: React.Dispatch<React.SetStateAction<any>>) => (value: any) => {
      setter(value);
      setPage(0); // Reset to the first page when a filter changes
    };
  const handleSelectRider = (email: string) => {
    setSelectedRiders((prev) =>
      prev.includes(email) ? prev.filter((riderEmail) => riderEmail !== email) : [...prev, email]
    );
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = riders.map((rider) => rider.email);
      setSelectedRiders(allIds);
    } else {
      setSelectedRiders([]);
    }
  };

  useEffect(() => {
    onSelectionChange?.(riders.filter((rider) => selectedRiders.includes(rider.email)));
  }, [selectedRiders, riders]);

  const handleNotification = (type: "sms" | "email" | "push") => {
    console.log(`Sending ${type} to:`, selectedRiders);
    // Call the appropriate API to send notifications here
  };

  return (
    <Box marginTop={"10px"}>
      {/* Toggle Filters Button */}
      <Button
        variant="outlined"
        onClick={() => setFiltersVisible((prev) => !prev)}
        sx={{ marginBottom: 2 }}
      >
        {filtersVisible ? "Hide Filters" : "Show Filters"}
      </Button>
      {/* Filters section */}
      {filtersVisible && (
        <Box sx={{ display: "flex", gap: 2, marginBottom: 2, flexWrap: "wrap" }}>
          <TextField
            label="Search Riders by name, email & phone number"
            value={search}
            onChange={handleSearchChange}
            fullWidth
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={role}
              onChange={(e) => handleFilterChange(setRole)(e.target.value)}
              label="Role"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="pro-user">Pro-User</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Min Total Rides"
            type="number"
            value={minRides}
            onChange={(e) => handleFilterChange(setMinRides)(e.target.value ? +e.target.value : "")}
          />
          <TextField
            label="Max Total Rides"
            type="number"
            value={maxRides}
            onChange={(e) => handleFilterChange(setMaxRides)(e.target.value ? +e.target.value : "")}
          />
          {/* Country Filter */}
          {adminCountry === "all" && (
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Country</InputLabel>
              <Select
                value={country}
                onChange={(e) => handleFilterChange(setCountry)(e.target.value)}
                label="Country"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="uk">UK</MenuItem>
                <MenuItem value="france">France</MenuItem>
                <MenuItem value="ivory-coast">Ivory Coast</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
      )}
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        showLastButton
        showFirstButton
      />
      <TableContainer component={Paper}>
        <Table>
          <TableHead style={{ backgroundColor: "rgb(224, 224, 224)" }}>
            <TableRow>
              <TableCell padding="checkbox"></TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Total Ride Count</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Signed up at</TableCell>
              <TableCell>Referral Count</TableCell> {/* Added here */}
            </TableRow>
          </TableHead>
          <TableBody>
            {riders.map((rider) => (
              <TableRow key={rider._id} selected={selectedRiders.includes(rider._id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedRiders.includes(rider.email)}
                    onChange={() => handleSelectRider(rider.email)}
                  />
                </TableCell>
                <TableCell>{`${rider.firstName} ${rider.lastName}`}</TableCell>
                <TableCell>{rider.email}</TableCell>
                <TableCell>{rider.phone}</TableCell>
                <TableCell>{rider.totalRideCount || 0}</TableCell>
                <TableCell>{rider.countryCode ? countries[rider.countryCode] : "-"}</TableCell>
                <TableCell>{rider.role}</TableCell>
                <TableCell>{formatTime(rider.createdAt)}</TableCell>
                <TableCell
                  style={{
                    cursor:
                      rider.referral_count && rider.referral_count > 0 ? "pointer" : "default",
                    color: rider.referral_count && rider.referral_count > 0 ? "blue" : "black",
                  }}
                  onClick={
                    rider.referral_count && rider.referral_count > 0
                      ? () => handleReferralClick(rider._id)
                      : undefined
                  }
                >
                  {rider.referral_count || 0}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Checkbox
            indeterminate={selectedRiders.length > 0 && selectedRiders.length < riders.length}
            checked={selectedRiders.length === riders.length && riders.length > 0}
            onChange={handleSelectAll}
          />
          <Box component="span" sx={{ fontSize: "0.875rem" }}>
            Select All {selectedRiders.length > 0 && `(${selectedRiders.length})`}
          </Box>
        </Box>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          showLastButton
          showFirstButton
        />
      </Box>
      <EditorProvider>
        <NotificationPopup
          disabled={selectedRiders.length === 0}
          selectedUsers={riders.filter((rider) => selectedRiders.includes(rider.email))}
        ></NotificationPopup>
      </EditorProvider>
      {/* Popup for Referred Users */}
      <Dialog open={popupOpen} onClose={handleClosePopup} fullWidth maxWidth="sm">
        <DialogTitle>Referred Users</DialogTitle>
        <DialogContent>
          {loading ? (
            <CircularProgress />
          ) : referredUsers.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>User Type</TableCell>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>Total Rides</TableCell>
                  <TableCell>Rejected Rides</TableCell>
                  <TableCell>Referral Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {referredUsers.map((user) => (
                  <TableRow key={user.referred_user_id}>
                    <TableCell>{user.referred_user_name}</TableCell>
                    <TableCell>{user.user_type}</TableCell>
                    <TableCell>{user.phone_number}</TableCell>
                    <TableCell>{user.total_ride_count}</TableCell>
                    <TableCell>{user.total_rejected_ride_count}</TableCell>
                    <TableCell>{formatTime(user.referral_date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p>No referred users found.</p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePopup}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RiderTable;
